import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useSearchParams } from "react-router-dom";
import { api } from "@/api";
import { toast } from "sonner";
import { Zap, MessageSquareReply, CheckCheck, XCircle, FlaskConical, ChevronRight, ChevronDown } from "lucide-react";

const CLASS_STYLES = {
  material_reply: "bg-pink-950/60 text-pink-300 border-pink-800/60",
  question: "bg-amber-950/60 text-amber-300 border-amber-800/60",
  neutral: "bg-slate-800/60 text-slate-400 border-slate-700",
  not_interested: "bg-rose-950/60 text-rose-300 border-rose-800/60",
};

function ConversationDetail({ cid, invalidate }) {
  const [simBody, setSimBody] = useState("");
  const { data: detail, isLoading } = useQuery({
    queryKey: ["conversation", cid],
    queryFn: async () => (await api.get(`/conversations/${cid}`)).data,
  });
  const classify = useMutation({
    mutationFn: async () => (await api.post(`/conversations/${cid}/classify`)).data,
    onSuccess: (r) => { toast.success(`Classified: ${r.classification.category}`); invalidate(); },
    onError: (e) => toast.error(e.response?.data?.detail?.message || e.response?.data?.detail || "Classification failed"),
  });
  const decide = useMutation({
    mutationFn: async (payload) => (await api.post("/decisions", payload)).data,
    onSuccess: () => { toast.success("Decision recorded"); invalidate(); },
    onError: (e) => toast.error(e.response?.data?.detail?.message || e.response?.data?.detail || "Decision failed"),
  });
  const simulate = useMutation({
    mutationFn: async () => (await api.post("/events/reply_received", { conversation_id: cid, body: simBody })).data,
    onSuccess: (r) => { toast.success(`Reply ingested — ${r.classification.category}`); setSimBody(""); invalidate(); },
    onError: (e) => toast.error(e.response?.data?.detail?.message || e.response?.data?.detail || "Simulation failed"),
  });
  if (isLoading || !detail) return <div className="text-xs text-slate-500 py-2">Loading conversation…</div>;
  return (
    <div className="space-y-4" data-testid="conversation-detail-panel">
      <div className="flex items-start justify-between gap-3">
        <div>
          <h2 className="text-lg font-medium text-slate-200 tracking-tight">{detail.prospect?.name}</h2>
          <p className="text-xs text-slate-400">{detail.contact?.name} · {detail.channel}</p>
        </div>
        {detail.classification && (
          <span className={`inline-flex px-2 py-0.5 rounded text-[11px] font-mono border ${CLASS_STYLES[detail.classification] || CLASS_STYLES.neutral}`} data-testid="conversation-classification">
            {detail.classification.replace("_", " ")}
          </span>
        )}
      </div>

      <div className="space-y-2 max-h-80 overflow-y-auto pr-1" data-testid="message-thread">
        {detail.messages?.map((m) => (
          <div key={m.id} data-testid={`message-${m.id}`}
            className={`rounded-md border p-3 text-sm leading-relaxed ${m.direction === "inbound" ? "border-slate-700 bg-slate-800/50 text-slate-200 mr-8" : "border-slate-800 bg-slate-950/60 text-slate-400 ml-8"}`}>
            <div className="font-mono text-[10px] uppercase tracking-widest text-slate-500 mb-1">
              {m.author} · {m.direction} · {new Date(m.created_at).toLocaleString()}
            </div>
            {m.body}
          </div>
        ))}
      </div>

      {detail.recommended_action && (
        <div className="border border-indigo-800/50 bg-indigo-950/30 rounded-md px-3 py-2 text-xs text-indigo-300" data-testid="recommended-action">
          Recommended next action: <span className="font-mono">{detail.recommended_action}</span>
        </div>
      )}

      <div className="flex flex-wrap gap-2 pt-2 border-t border-slate-800/60">
        <button onClick={() => classify.mutate()} disabled={classify.isPending} data-testid="classify-reply-btn"
          className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium border bg-[#312E81] text-[#A5B4FC] border-[#4F46E5] hover:brightness-125 transition-all disabled:opacity-40">
          <MessageSquareReply size={12} /> Classify Latest Reply
        </button>
        <button onClick={() => decide.mutate({ action: "follow_up", target_type: "conversation", target_id: detail.id })} data-testid="follow-up-btn"
          className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium border bg-[#0C4A6E] text-[#38BDF8] border-[#0284C7] hover:brightness-125 transition-all">
          <CheckCheck size={12} /> Follow Up
        </button>
        <button onClick={() => decide.mutate({ action: "close_conversation", target_type: "conversation", target_id: detail.id })} data-testid="close-conversation-btn"
          className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium border bg-[#7F1D1D] text-[#F87171] border-[#DC2626] hover:brightness-125 transition-all">
          <XCircle size={12} /> Close Conversation
        </button>
      </div>

      <div className="border border-slate-800 rounded-md p-3 space-y-2 bg-slate-950/40">
        <div className="flex items-center gap-1.5 font-mono text-[10px] uppercase tracking-widest text-slate-500">
          <FlaskConical size={11} /> Dev simulator — ingest inbound reply (mocked)
        </div>
        <textarea value={simBody} onChange={(e) => setSimBody(e.target.value)} rows={2} placeholder="e.g. We're interested — can we schedule a call this week?" data-testid="simulate-reply-input"
          className="w-full bg-slate-900 border border-slate-700 rounded-md p-2.5 text-sm text-slate-200 placeholder:text-slate-600 focus:outline-none focus:ring-1 focus:ring-sky-500" />
        <button onClick={() => simulate.mutate()} disabled={simulate.isPending || !simBody.trim()} data-testid="simulate-reply-btn"
          className="px-3 py-1.5 rounded-md text-xs font-medium border bg-slate-800 text-slate-300 border-slate-600 hover:bg-slate-700 transition-colors disabled:opacity-40">
          Ingest Reply
        </button>
      </div>
    </div>
  );
}

export default function Conversations() {
  const [params, setParams] = useSearchParams();
  const selectedId = params.get("selected");
  const qc = useQueryClient();

  const { data: conversations = [], isLoading } = useQuery({
    queryKey: ["conversations"],
    queryFn: async () => (await api.get("/conversations")).data,
  });

  const invalidate = () => {
    qc.invalidateQueries({ queryKey: ["conversations"] });
    qc.invalidateQueries({ queryKey: ["conversation", selectedId] });
    qc.invalidateQueries({ queryKey: ["dashboard"] });
  };

  const toggle = (id) => setParams(selectedId === id ? {} : { selected: id });

  return (
    <div className="space-y-6" data-testid="conversations-view">
      <div>
        <h1 className="text-2xl sm:text-3xl font-semibold tracking-tight text-slate-100">Conversations</h1>
        <p className="text-sm text-slate-400 mt-1">Threaded email + LinkedIn responses with material-reply flags and next actions.</p>
      </div>

      <div className="space-y-2" data-testid="conversations-list">
        {isLoading && <p className="text-sm text-slate-500">Loading…</p>}
        {conversations.length === 0 && !isLoading && <p className="text-sm text-slate-500">No conversations yet.</p>}
        {conversations.map((c) => {
          const open = selectedId === c.id;
          return (
            <div key={c.id} className={`border rounded-lg transition-all duration-200 ${open ? "border-sky-800/70 bg-slate-900" : "border-slate-800/80 bg-slate-900/60 hover:border-slate-700"}`}>
              <button onClick={() => toggle(c.id)} data-testid={`conversation-item-${c.id}`}
                className="w-full text-left p-4 space-y-1.5">
                <div className="flex items-center justify-between gap-2">
                  <span className="text-sm font-medium text-slate-200 flex items-center gap-2">
                    {open ? <ChevronDown size={12} className="text-slate-500" /> : <ChevronRight size={12} className="text-slate-500" />}
                    {c.prospect_name}
                  </span>
                  <div className="flex items-center gap-1.5">
                    {c.material_reply && !c.material_handled && (
                      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-[10px] font-mono border bg-pink-950/60 text-pink-300 border-pink-800/60" data-testid={`material-flag-${c.id}`}>
                        <Zap size={9} /> material reply
                      </span>
                    )}
                    <span className={`font-mono text-[10px] uppercase tracking-wider ${c.status === "open" ? "text-emerald-400" : "text-slate-500"}`}>{c.status}</span>
                  </div>
                </div>
                <div className="text-xs text-slate-400 pl-5">{c.contact_name} · {c.channel}</div>
                {c.last_message && <div className="text-xs text-slate-500 truncate pl-5">{c.last_message.body}</div>}
              </button>
              {open && (
                <div className="border-t border-slate-800 p-4 bg-slate-950/40">
                  <ConversationDetail cid={c.id} invalidate={invalidate} />
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
